import { type DataNeededId } from "@inrev/common";
import { useFetchAccountAttachments } from "@portal/api/account";
import { type DataNeeded } from "@portal/api/decisioning";
import { useResolveDataNeeded } from "@portal/api/decisioning/admin";
import { type FC, useEffect, useMemo, useState } from "react";
import { MultiSelectDropdownInput } from "../../../../form/inputs/MultiSelectDropdownInput";
import { TextAreaInput } from "../../../../form/inputs/TextAreaInput";
import { PanelSheet } from "../../../../patterns/panel-sheet";
import { ClickableText, Text } from "../../../../primitives";
import { useDecisioningData } from "../DecisioningDataContext";
import { type DecisioningSheet, useDecisioningPanel } from "../DecisioningPanelContext";
import { PanelField } from "../PanelField";
import {
	attachmentOptionLabel,
	matchingAttachments,
	prefilledAttachmentIds,
	resolveIssue,
	toReceivedResolution,
} from "./resolveDraft";

type ResolveSheet = Extract<DecisioningSheet, { kind: "resolve" }>;

const ResolveSheetForm: FC<{ item: DataNeeded }> = ({ item }) => {
	const { accountId, closeSheet, toastOnSave } = useDecisioningPanel();
	const { resolveDataNeededAsync } = useResolveDataNeeded(accountId);
	const { attachments, attachmentsError, attachmentsIsLoading } =
		useFetchAccountAttachments(accountId);

	const { type, title } = item;
	const prefilled = useMemo(() => prefilledAttachmentIds(item, attachments), [item, attachments]);
	const [picked, setPicked] = useState<string[] | null>(null);
	const [answer, setAnswer] = useState("");
	const selectedIds = picked ?? prefilled;

	const matching = useMemo(() => matchingAttachments(item, attachments), [item, attachments]);
	const [showAll, setShowAll] = useState(false);
	const narrowed = matching !== null && !showAll;

	const options = useMemo(() => {
		const visibleIds = new Set((matching ?? []).map(({ id }) => id));
		return attachments
			.filter((attachment) => !narrowed || visibleIds.has(attachment.id))
			.sort((a, b) => b.createdAtMs - a.createdAtMs)
			.map((attachment) => ({
				value: attachment.id,
				label: attachmentOptionLabel(attachment),
			}));
	}, [attachments, matching, narrowed]);

	/** Narrowing again drops picks the narrowed list no longer shows, so none save unseen. */
	const toggleShowAll = () => {
		if (showAll && matching !== null) {
			const matchingIds = new Set<string>(matching.map(({ id }) => id));
			setPicked(selectedIds.filter((id) => matchingIds.has(id)));
		}
		setShowAll((prev) => !prev);
	};

	const issue = resolveIssue(type, { attachmentCount: selectedIds.length, answer });
	const blockedByLoad = type === "Document" && (attachmentsIsLoading || attachmentsError !== null);

	const status = (() => {
		if (type === "Document" && attachmentsIsLoading) {
			return { text: "Loading attachments…", tone: "default" as const };
		}
		if (type === "Document" && attachmentsError !== null) {
			return { text: "Could not load attachments.", tone: "danger" as const };
		}
		if (issue) return { text: issue, tone: "danger" as const };
		return { text: `Marks ${title} received.`, tone: "default" as const };
	})();

	const save = () => {
		if (issue || blockedByLoad) return;
		const attachmentIds = attachments
			.filter((attachment) => selectedIds.includes(attachment.id))
			.map((attachment) => attachment.id);
		closeSheet();
		toastOnSave(
			resolveDataNeededAsync({
				id: item.id,
				resolution: toReceivedResolution(type, { attachmentIds, answer }),
			}),
			`Marked ${title} received`,
		);
	};

	const canWiden = matching !== null && attachments.length > matching.length;
	const attachmentHelper = (() => {
		if (attachmentsIsLoading || attachmentsError !== null) return undefined;
		if (narrowed && matching.length === 0) {
			return "No matching files uploaded since this was requested.";
		}
		if (type === "Document" && prefilled.length > 0 && picked === null) {
			return "Pre-selected from matching files uploaded since the request.";
		}
		return undefined;
	})();

	const attachmentField = (
		<div className="flex flex-col gap-1.5">
			<PanelField
				label={type === "Document" ? "Attachments" : "Attachments (optional)"}
				required={type === "Document"}
				helper={attachmentHelper}
				testId="resolve-attachments"
			>
				{({ id, labelId }) => (
					<MultiSelectDropdownInput
						buttonId={id}
						aria-labelledby={labelId}
						compact
						anchor="bottom start"
						optionsClassName="w-[var(--button-width)] max-w-[var(--button-width)] break-words"
						testId="resolve-attachments-select"
						value={selectedIds}
						options={options}
						onChange={setPicked}
						disabled={attachmentsIsLoading}
						placeholder={
							options.length > 0
								? "Select files"
								: narrowed
									? "No matching files"
									: "No attachments on this account"
						}
						error={type === "Document" && issue !== null}
					/>
				)}
			</PanelField>
			{canWiden && !attachmentsIsLoading && (
				<ClickableText
					type="caption"
					color="link"
					className="self-start"
					testId="resolve-attachments-show-all"
					onClick={toggleShowAll}
				>
					{showAll ? "Show only matching files" : "Show all attachments"}
				</ClickableText>
			)}
		</div>
	);

	return (
		<PanelSheet
			title="Mark received"
			testId="resolve-data-needed-sheet"
			status={status.text}
			statusTone={status.tone}
			onCancel={closeSheet}
			dirty={picked !== null || answer !== ""}
			primaryLabel="Mark received"
			primaryDisabled={issue !== null || blockedByLoad}
			onPrimary={save}
		>
			<Text type="body" color="primary" className="block font-medium">
				{title}
			</Text>
			{type === "Document" && attachmentField}
			{type === "Question" && (
				<>
					<PanelField label="Answer" required testId="resolve-answer-field">
						{({ id }) => (
							<TextAreaInput
								id={id}
								className="h-[96px]"
								value={answer}
								placeholder="What did you learn?"
								invalid={issue !== null}
								onChange={(event) => setAnswer(event.target.value ?? "")}
								data-testid="resolve-answer"
							/>
						)}
					</PanelField>
					{attachmentField}
				</>
			)}
			{type === "Task" && (
				<Text type="body" color="secondary">
					Confirm this task is done.
				</Text>
			)}
		</PanelSheet>
	);
};

export const ResolveDataNeededSheet: FC<{ sheet: ResolveSheet }> = ({ sheet }) => {
	const { dataNeeded } = useDecisioningData();
	const { closeSheet } = useDecisioningPanel();
	const dataNeededId: DataNeededId = sheet.dataNeededId;
	const item = dataNeeded.find((candidate) => candidate.id === dataNeededId);

	useEffect(() => {
		if (item === undefined) closeSheet();
	}, [item, closeSheet]);

	if (item === undefined) return null;

	return <ResolveSheetForm key={item.id} item={item} />;
};
